import { useState, useEffect, useCallback } from "react";
import { opApi } from "@/lib/api";
import { money } from "@/components/mc/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, Loader2, Receipt, Printer } from "lucide-react";

function localToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function Row({ label, value, strong, accent }) {
  return (
    <div className={`flex justify-between py-1.5 ${strong ? "font-bold text-base" : "text-sm"}`}>
      <span className={strong ? "" : "text-muted-foreground"}>{label}</span>
      <span className={`font-mono ${accent || ""}`}>{value}</span>
    </div>
  );
}

export function ZReport() {
  const [date, setDate] = useState(localToday());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((d) => {
    setLoading(true);
    opApi.get(`/tenant/zreport?date=${d}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(date); }, [date, load]);

  const isToday = date === localToday();

  return (
    <div data-testid="zreport">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <p className="text-xs text-muted-foreground max-w-lg">
          Your close-out summary builds itself from the day's tickets — no manual "run report" needed. Pick a date to review any day.
        </p>
        <div className="flex items-center gap-2">
          <Input type="date" max={localToday()} value={date} onChange={(e) => setDate(e.target.value)} className="rounded-none h-9 w-auto text-sm" data-testid="zreport-date" />
          <Button variant="outline" size="icon" className="rounded-none h-9 w-9" onClick={() => load(date)} data-testid="zreport-refresh"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2 py-8"><Loader2 className="h-4 w-4 animate-spin" /> Building report…</div>
      ) : !data ? (
        <div className="border border-dashed border-border py-8 text-center text-sm text-muted-foreground">Couldn't load the report.</div>
      ) : (
        <div className="border border-border bg-card p-5 max-w-lg" data-testid="zreport-card">
          <div className="flex items-center gap-2 border-b border-border pb-3 mb-3">
            <Receipt className="h-5 w-5 text-primary" />
            <div>
              <div className="font-display font-bold text-lg leading-none">Z-Report {isToday && <span className="text-xs font-normal text-emerald-600 uppercase tracking-wider ml-1">· Today (live)</span>}</div>
              <div className="text-xs text-muted-foreground mt-1">{data.date} · {data.order_count} order{data.order_count === 1 ? "" : "s"}</div>
            </div>
          </div>

          <Row label="Gross sales" value={money(data.gross_sales)} />
          <Row label="Discounts given" value={`−${money(data.discounts)}`} accent={data.discounts > 0 ? "text-emerald-700" : ""} />
          <Row label="Net sales" value={money(data.net_sales)} />
          <Row label="Tax collected" value={money(data.tax_collected)} />
          <div className="border-t border-border my-1" />
          <Row label="Total revenue" value={money(data.total_revenue)} strong />
          <div className="border-t border-border my-2" />
          <Row label="Collected (paid)" value={money(data.collected)} accent="text-emerald-700" />
          <Row label="Cash owed / outstanding" value={money(data.outstanding)} accent={data.outstanding > 0 ? "text-red-600" : ""} />
          <div className="flex gap-4 text-xs text-muted-foreground pt-2">
            <span data-testid="zreport-rewards">{data.reward_redemptions} reward{data.reward_redemptions === 1 ? "" : "s"} redeemed</span>
            <span data-testid="zreport-promos">{data.promo_redemptions} promo{data.promo_redemptions === 1 ? "" : "s"} used</span>
          </div>

          {data.by_employee.length > 0 && (
            <div className="mt-4 pt-3 border-t border-border">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">By employee</div>
              <div className="space-y-1">
                {data.by_employee.map((e, i) => (
                  <div key={i} className="flex justify-between items-baseline text-sm" data-testid={`zreport-emp-${i}`}>
                    <span className="truncate">{e.name} <span className="text-xs text-muted-foreground">· {e.orders}</span></span>
                    <span className="font-mono">{money(e.sales)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Button variant="outline" size="sm" className="rounded-none gap-1.5 mt-4 w-full" onClick={() => window.print()} data-testid="zreport-print">
            <Printer className="h-4 w-4" /> Print report
          </Button>
        </div>
      )}
    </div>
  );
}
